/**
 * React bindings for the PlanStore. Fields subscribe individually, so typing in
 * one cell of a 400-field form re-renders that cell only.
 */
import { createContext, useCallback, useContext, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import {
  FIELD_SEP, PlanStore,
  type DerivedSource, type FieldFlag, type RouteMark, type UiWarning, type WarnRef,
  type TerrainStatus, type WindsStatus, type WxStatus,
} from './store';
import type { RouteDoc } from './route';

const PlanContext = createContext<PlanStore | null>(null);

export function PlanProvider({ store, children }: { store: PlanStore; children: ReactNode }) {
  return <PlanContext.Provider value={store}>{children}</PlanContext.Provider>;
}

export function usePlanStore(): PlanStore {
  const s = useContext(PlanContext);
  if (!s) throw new Error('usePlanStore outside a <PlanProvider>');
  return s;
}

export interface FieldView {
  value: string;
  /** '' | 'warn' | 'warn2' */
  flag: FieldFlag;
  /** the value is computed (petrol) rather than typed */
  auto: boolean;
  /** '' | 's' route-derived | 'o' route-derived but edited here */
  route: RouteMark;
  /** '' | 'aircraft' | 'aircraft-est' | 'wx' | 'geo' | 'winds' | 'terrain' — which non-pilot source filled the cell and still owns it */
  derived: DerivedSource;
  /** what to call that source in the tooltip: a registration, a METAR station, an ICAO code */
  derivedFrom: string;
  /** the warning attached to this field, by code — translated at render time */
  warn: WarnRef | null;
}

/** The `{params, fallback}` half of a field snapshot; anything unreadable is dropped. */
function parseWarn(code: string, json: string): WarnRef | null {
  if (!code) return null;
  try {
    const extra = (json ? JSON.parse(json) : {}) as { params?: Record<string, string>; fallback?: string };
    return {
      code,
      ...(extra.params ? { params: extra.params } : {}),
      ...(extra.fallback !== undefined ? { fallback: extra.fallback } : {}),
    };
  } catch {
    return { code };
  }
}

/** Subscribe to one field's value, computed-ness and warning state. */
export function useField(id: string): FieldView {
  const store = usePlanStore();
  const subscribe = useMemo(() => store.subscribeField(id), [store, id]);
  const getSnapshot = useCallback(() => store.fieldState(id), [store, id]);
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return useMemo(() => {
    const [flag = '', auto = '0', route = '', derived = '', from = '', code = '', json = '', ...rest] =
      state.split(FIELD_SEP);
    return {
      flag: flag as FieldFlag,
      auto: auto === '1',
      route: route as RouteMark,
      derived: derived as DerivedSource,
      derivedFrom: from,
      warn: parseWarn(code, json),
      value: rest.join(FIELD_SEP),
    };
  }, [state]);
}

/** The plan's waypoint list, re-read whenever the document changes. */
export function useRoute(): RouteDoc | null {
  const store = usePlanStore();
  const rev = usePlanRev();
  return useMemo(() => store.route, [store, rev]);
}

/**
 * The provenance of the plan's METAR/TAF briefing (`plans.meta.wx`). Read from
 * the store rather than from the fetch, so the shared read-only view — which
 * never fetches — shows the same line from the stored document.
 */
export function useWxStatus(): WxStatus | null {
  const store = usePlanStore();
  const rev = usePlanRev();
  return useMemo(() => store.wxStatus(), [store, rev]);
}

/**
 * The provenance of the plan's winds-aloft fill (`plans.meta.winds`). Read from
 * the store for the same reason the weather's is: the shared read-only view has
 * to print the attribution and the forecast hour without fetching anything.
 */
export function useWindsStatus(): WindsStatus | null {
  const store = usePlanStore();
  const rev = usePlanRev();
  return useMemo(() => store.windsStatus(), [store, rev]);
}

/**
 * What the plan remembers of its terrain profile (`plans.meta.terrain`). Read
 * from the store, like the weather's and the winds', so the shared read-only
 * view can print the summary and the attribution without fetching anything.
 */
export function useTerrainStatus(): TerrainStatus | null {
  const store = usePlanStore();
  const rev = usePlanRev();
  return useMemo(() => store.terrainStatus(), [store, rev]);
}

/** Document revision — bumped on every change; drives autosave effects. */
export function usePlanRev(): number {
  const store = usePlanStore();
  return useSyncExternalStore(store.subscribe, store.getRev, store.getRev);
}

export function useWarnings(): { warnings: UiWarning[]; red: boolean } {
  const store = usePlanStore();
  const rev = usePlanRev();
  return useMemo(() => ({ warnings: store.warnings, red: store.red }), [store, rev]);
}
